import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, reactive, ref, Ref } from 'vue';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { email, maxLength, required, requiredIf } from '@/utils/validators';
import { ExclamationIcon } from '@heroicons/vue/solid';

import router from '@/router';
import { useAuthStore, useUiStore } from '@/stores';
import AuthService from '@/services/api/AuthService';
import { UserService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

import { AuthServiceLoginResult, LoginFormState, UserModel } from '@/types';
import axios from 'axios';
enum FormType {
    LOGIN = 'LOGIN',
    RECOVER = 'RECOVER',
    SENT_RECOVER = 'SENT_RECOVER',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginPage',
  setup(__props, { expose: __expose }) {
  __expose();

const uiStore = useUiStore();
const authStore = useAuthStore();

const showMessageUserBlocked = ref(false);

const formState = reactive<LoginFormState>({
    email: '',
    password: '',
    rememberMe: true,
});

const showFormType = ref<FormType>(FormType.LOGIN);

const user: ComputedRef<UserModel | undefined> = computed(() => authStore.userModel);

const rules = computed<ValidationArgs<LoginFormState>>(() => ({
    email: {
        email,
        required,
        maxLength: maxLength(250),
    },
    password: {
        required: requiredIf(() => showFormType.value === FormType.LOGIN),
    },
    rememberMe: {},
}));

const v$: Ref<Validation<ValidationArgs<LoginFormState>>> = useVuelidate(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const handleFormValidation = () => v$.value.$validate();

const handleLoginSuccess = async (response: AuthServiceLoginResult) => {
    authStore.login(response);

    if (user.value !== undefined) {
        if (authStore.userNeedsUpdatePassword) {
            await router.push({ name: 'UpdatePassword' });
            return;
        }

        if (user.value.requiresTwoFactor) {
            await router.push({ name: 'TwoFaAuthenticator' });
            return;
        }
    }
    await router.push('/');
};

const onSubmitLogin = async () => {
    if (!(await handleFormValidation())) return;

    try {
        setLoading(true);
        const response = await AuthService.login(formState);
        await handleLoginSuccess(response.data);
    } catch (error) {
        if (axios.isAxiosError(error) && error?.response?.status === 423) {
            showMessageUserBlocked.value = true;
        } else {
            toastServiceError(error);
        }
    } finally {
        setLoading(false);
    }
};

const onSubmitResetPassword = async () => {
    if (!(await handleFormValidation())) return;

    try {
        setLoading(true);
        await UserService.recoveryPassword(formState);
        showFormType.value = FormType.SENT_RECOVER;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const __returned__ = { FormType, uiStore, authStore, showMessageUserBlocked, formState, showFormType, user, rules, v$, setLoading, handleFormValidation, handleLoginSuccess, onSubmitLogin, onSubmitResetPassword, get ExclamationIcon() { return ExclamationIcon } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})